import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { observable, runInAction } from 'mobx'
import type RelationManager from '@/features/relation-manager/RelationManager'
import { Id } from '@/infra/id'
import type {
  FetchRelationManagerFn,
  FetchAgentsEnabledFn,
} from '@/features/relation-manager'
import type { GetCurrentWorkspaceFn } from '@/features/iam'
import { ignoreAbortError } from '@/utils/abort-util'
import { NotFound } from 'fejl'
import { type JobType, TaxJobType } from '@/features/jobs/job-type'

export class JobDetailsRelationManagerViewModel {
  private readonly _lifecycle = new LifecycleTracker()

  @observable
  public accessor relationManager: RelationManager | null = null

  /**
   * Whether the relation manager should be shown for the current job.
   */
  @observable
  public accessor isEnabled = false

  constructor(
    private readonly jobId: Id,
    private readonly jobType: JobType,
    private readonly fetchRelationManager: FetchRelationManagerFn,
    private readonly fetchAgentsEnabled: FetchAgentsEnabledFn,
    private readonly getCurrentWorkspace: GetCurrentWorkspaceFn,
  ) {}

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()

    if (!(this.jobType instanceof TaxJobType)) {
      return
    }

    void this.load(this._lifecycle.signal).catch(ignoreAbortError)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Resolves whether agents are enabled and loads the
   * relation manager for the current job if so.
   */
  private async load(signal?: AbortSignal) {
    const enabled = await this.fetchAgentsEnabled(
      Id.assert(this.getCurrentWorkspace().id),
    )

    signal?.throwIfAborted()
    runInAction(() => (this.isEnabled = enabled))

    if (!this.isEnabled) {
      return
    }

    await this.getRelationManager(signal)
  }

  /**
   * Fetches the relation manager for the current job and stores it.
   */
  async getRelationManager(signal?: AbortSignal) {
    const relationManager = await this.fetchRelationManager(
      Id.assert(this.jobId),
    ).catch(NotFound.ignore(null))

    signal?.throwIfAborted()

    runInAction(() => (this.relationManager = relationManager))
  }
}
